import type { TenantModuleKey, TenantModuleRow } from "@/hooks/useTenantCapabilities";
import { hasModule, isTenantModuleKey } from "@/hooks/useTenantCapabilities";

/**
 * V3 — frontend-only tenant module enforcement.
 *
 * This maps app routes to the module that owns them. It is UX enforcement only:
 * backend/RLS/edge-function enforcement is deliberately NOT part of V3.
 *
 * Surfaces intentionally NOT mapped (always available): Super Admin, account,
 * dashboard, order management, POS Sync, contacts, locations, reports,
 * monitoring/data health, admin management pages, public routes and auth.
 */

/** Exact-path route → module map. Prefix routes are listed separately below. */
export const MODULE_ROUTES: Record<string, TenantModuleKey> = {
  // factory_portal
  "/factory-auth": "factory_portal",
  "/factory-user-auth": "factory_portal",
  "/factory-portal": "factory_portal",

  // outlet_portal
  "/outlet-auth": "outlet_portal",
  "/outlet-user-auth": "outlet_portal",
  "/outlet-portal": "outlet_portal",

  // logistics
  "/tasks-list": "logistics",
  "/tasks": "logistics",
  "/app/tasks": "logistics",
  "/route-management": "logistics",
  "/fleet/vehicles": "logistics",
  "/logistics-requests": "logistics",
  "/logistics-request": "logistics",
  "/drivers-management": "logistics",
  "/drivers": "logistics",
  "/app/drivers": "logistics",
  "/map": "logistics",
  "/app/map": "logistics",
  "/track": "logistics",
  "/operations": "logistics",
};

/** Path prefixes (sub-routes) → module. */
export const MODULE_ROUTE_PREFIXES: Array<[string, TenantModuleKey]> = [
  ["/driver", "logistics"],
  ["/tasks/", "logistics"],
  ["/app/tasks/", "logistics"],
];

/** Returns the owning module for a path, or null when the route is unguarded. */
export function getModuleForPath(pathname: string): TenantModuleKey | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const exact = MODULE_ROUTES[path];
  if (exact) return exact;
  for (const [prefix, key] of MODULE_ROUTE_PREFIXES) {
    if (path === prefix || path.startsWith(prefix.endsWith("/") ? prefix : `${prefix}/`)) {
      return key;
    }
  }
  return null;
}

export type ModuleAccessDecision = "loading" | "allow" | "blocked";

export interface ModuleAccessInput {
  moduleKey: string;
  /** True once a tenant is resolvable for the current session. */
  tenantKnown: boolean;
  /** Auth/tenant/entitlement queries still settling. */
  isLoading: boolean;
  /** Entitlement rows for the tenant (fail-open when missing). */
  rows: TenantModuleRow[] | null | undefined;
  /** Entitlement query failed — must fail open. */
  hasError?: boolean;
}

/**
 * Pure guard decision. Fail-open by contract:
 *  - unknown module key                → blocked (never a valid surface)
 *  - tenant not yet known              → allow (portal sign-in must not break)
 *  - still loading                     → loading (no flash of denial)
 *  - query error / missing legacy rows → allow
 *  - explicit enabled = false          → blocked
 */
export function decideModuleAccess(input: ModuleAccessInput): ModuleAccessDecision {
  const { moduleKey, tenantKnown, isLoading, rows, hasError } = input;
  if (!isTenantModuleKey(moduleKey)) return "blocked";
  if (!tenantKnown) return "allow";
  if (isLoading) return "loading";
  if (hasError) return "allow";
  return hasModule(rows, moduleKey) ? "allow" : "blocked";
}

/** Convenience for navigation filtering — a nav url is hidden only when blocked. */
export function isNavItemModuleVisible(
  url: string,
  resolve: (key: TenantModuleKey) => boolean,
): boolean {
  const moduleKey = getModuleForPath(url);
  if (!moduleKey) return true;
  return resolve(moduleKey);
}

/**
 * Recovery CTA shown on the "module unavailable" card.
 *
 * UX decision (portal isolation): a blocked portal user must never be offered a
 * route into the main app. Factory/outlet portal surfaces only ever offer their
 * own sign-in page, and offer no CTA at all when the user is already sitting on
 * that sign-in path (avoids a self-referential link / redirect loop).
 * Every other module keeps the pre-existing dashboard CTA.
 */
export interface ModuleUnavailableRecovery {
  to: string;
  label: string;
}

const PORTAL_RECOVERY: Partial<Record<TenantModuleKey, ModuleUnavailableRecovery>> = {
  factory_portal: { to: "/factory-auth", label: "Return to Factory sign-in" },
  outlet_portal: { to: "/outlet-auth", label: "Return to Outlet sign-in" },
};

export function getModuleUnavailableRecovery(
  moduleKey: string,
  pathname?: string,
): ModuleUnavailableRecovery | null {
  const portal = isTenantModuleKey(moduleKey)
    ? PORTAL_RECOVERY[moduleKey as TenantModuleKey]
    : undefined;
  if (portal) {
    const path =
      pathname && pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
    if (path === portal.to) return null;
    return portal;
  }
  return { to: "/dashboard", label: "Go to dashboard" };
}
